import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { tradesAPI, usersAPI } from '../services/api';
import { BarChart, TrendingUp, TrendingDown, Activity, Settings, DollarSign, Users as UsersIcon } from 'lucide-react';
import ManualTradeModal from '../components/ManualTradeModal';

const Dashboard: React.FC = () => {
  const [showManualTrade, setShowManualTrade] = useState(false);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['tradeStats'],
    queryFn: () => tradesAPI.stats({ days: 7 }).then((res) => res.data)
  });

  const { data: recentTrades } = useQuery({
    queryKey: ['recentTrades'],
    queryFn: () => tradesAPI.list({ limit: 10 }).then((res) => res.data)
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersAPI.list().then((res) => res.data)
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Manual Trade Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Manual Trading</h3>
              <p className="text-blue-100 text-sm">Execute trades with custom parameters</p>
            </div>
            <DollarSign size={40} className="text-blue-200" />
          </div>
          <button
            onClick={() => setShowManualTrade(true)}
            className="w-full bg-white text-blue-600 font-semibold py-2 px-4 rounded-lg hover:bg-blue-50 transition"
          >
            Place Manual Trade
          </button>
        </div>

        {/* User Settings Card */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">User Settings</h3>
              <p className="text-purple-100 text-sm">Configure webhook settings & position sizes</p>
            </div>
            <Settings size={40} className="text-purple-200" />
          </div>

          {users && users.length > 0 ? (
            <div className="space-y-2">
              {users.slice(0, 3).map((user: any) => (
                <Link
                  key={user.id}
                  to={`/users?config=${user.id}`}
                  className="flex items-center justify-between w-full bg-white bg-opacity-20 hover:bg-opacity-30 py-2 px-3 rounded-lg transition"
                >
                  <div className="flex items-center gap-2">
                    <UsersIcon size={16} />
                    <span className="font-medium text-sm">{user.username}</span>
                  </div>
                  <span className="text-xs bg-white bg-opacity-30 px-2 py-1 rounded">
                    ₹{user.webhook_config?.default_position_size || 5000}
                  </span>
                </Link>
              ))}

              {users.length > 3 && (
                <Link
                  to="/users"
                  className="block text-center text-purple-100 hover:text-white text-sm mt-2"
                >
                  View all {users.length} users →
                </Link>
              )}
            </div>
          ) : (
            <Link
              to="/users"
              className="block w-full bg-white text-purple-600 font-semibold py-2 px-4 rounded-lg hover:bg-purple-50 transition text-center"
            >
              Create User
            </Link>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Trades</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.total_trades || 0}
              </p>
            </div>
            <Activity className="text-blue-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">
                {stats?.status_breakdown?.COMPLETED || 0}
              </p>
            </div>
            <TrendingUp className="text-green-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">
                {stats?.status_breakdown?.FAILED || 0}
              </p>
            </div>
            <TrendingDown className="text-red-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {stats?.status_breakdown?.PENDING || 0}
              </p>
            </div>
            <BarChart className="text-yellow-500" size={32} />
          </div>
        </div>
      </div>

      {/* Recent Trades */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Recent Trades</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Symbol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Time
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentTrades?.map((trade: any) => (
                <tr key={trade.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {trade.symbol}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span
                      className={`px-2 py-1 rounded ${
                        trade.transaction_type === 'BUY'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {trade.transaction_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {trade.quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {trade.price ? `₹${trade.price}` : 'MARKET'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`px-2 py-1 rounded ${
                        trade.status === 'COMPLETED'
                          ? 'bg-green-100 text-green-800'
                          : trade.status === 'FAILED'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {trade.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(trade.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual Trade Modal */}
      <ManualTradeModal
        isOpen={showManualTrade}
        onClose={() => setShowManualTrade(false)}
      />
    </div>
  );
};

export default Dashboard;
